import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [pricingReason, setPricingReason] = useState(null);
  const [pendingCheckoutKey, setPendingCheckoutKey] = useState(
    () => sessionStorage.getItem("vox_pending_checkout") || null
  );

  const clearPendingCheckout = useCallback(() => {
    sessionStorage.removeItem("vox_pending_checkout");
    setPendingCheckoutKey(null);
  }, []);

  const refreshUsage = useCallback(async () => {
    try {
      const { data } = await api.get("/usage");
      setUsage(data);
    } catch {
      setUsage(null);
    }
  }, []);

  const persist = useCallback(async (data) => {
    localStorage.setItem("vox_token", data.token);
    setUser(data.user);
    await refreshUsage();
  }, [refreshUsage]);

  const logout = useCallback(() => {
    localStorage.removeItem("vox_token");
    setUser(null);
    setUsage(null);
  }, []);

  useEffect(() => {
    const init = async () => {
      // Handle Emergent Google auth callback (#session_id=...) FIRST
      if (window.location.hash.includes("session_id=")) {
        const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
        window.history.replaceState({}, "", window.location.pathname + window.location.search);
        if (sid) {
          try {
            const { data } = await api.post("/auth/google", { session_id: sid });
            await persist(data);
            toast.success("Signed in with Google");
            navigate("/studio");
          } catch {
            toast.error("Google sign-in failed");
          }
        }
        setLoading(false);
        return;
      }
      const token = localStorage.getItem("vox_token");
      if (token) {
        try {
          const { data } = await api.get("/auth/me");
          setUser(data);
          await refreshUsage();
        } catch {
          logout();
        }
      }
      setLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    await persist(data);
    return data.user;
  }, [persist]);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    await persist(data);
    return data.user;
  }, [persist]);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const consume = useCallback(async (engine, chars) => {
    const { data } = await api.post("/usage/consume", { engine, chars });
    if (data.allowed && !data.unlimited) {
      setUsage((u) => (u ? { ...u, [engine]: { ...u[engine], used: data.used } } : u));
    }
    return data;
  }, []);

  const checkout = useCallback(async (lookupKey) => {
    const { data } = await api.post("/payments/checkout", {
      lookup_key: lookupKey,
    });
    window.location.href = data.checkout_url;
  }, []);

  const requestCheckout = useCallback(async (lookupKey) => {
    if (!user) {
      sessionStorage.setItem("vox_pending_checkout", lookupKey);
      setPendingCheckoutKey(lookupKey);
      setPricingOpen(false);
      setAuthOpen(true);
      return;
    }
    await checkout(lookupKey);
  }, [checkout, user]);

  useEffect(() => {
    if (!user || !pendingCheckoutKey) return;
    const lookupKey = pendingCheckoutKey;
    clearPendingCheckout();
    checkout(lookupKey).catch((error) => {
      toast.error(error?.response?.data?.detail || "Could not start checkout");
    });
  }, [checkout, clearPendingCheckout, pendingCheckoutKey, user]);

  const openBillingPortal = useCallback(async () => {
    const { data } = await api.post("/payments/portal");
    window.location.href = data.portal_url;
  }, []);

  const refundUsage = useCallback(async (chargeId) => {
    if (!chargeId) return false;
    const { data } = await api.post("/usage/refund", { charge_id: chargeId });
    if (data.refunded) await refreshUsage();
    return data.refunded;
  }, [refreshUsage]);

  const openAuth = useCallback(() => setAuthOpen(true), []);
  const openPricing = useCallback((reason) => {
    setPricingReason(reason || null);
    setPricingOpen(true);
  }, []);

  const value = {
    user,
    usage,
    loading,
    isPaid: !!usage?.unlimited,
    login,
    register,
    loginWithGoogle,
    logout,
    consume,
    checkout,
    requestCheckout,
    openBillingPortal,
    refundUsage,
    refreshUsage,
    authOpen,
    setAuthOpen,
    openAuth,
    pricingOpen,
    setPricingOpen,
    openPricing,
    pricingReason,
    cancelPendingCheckout: clearPendingCheckout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
